import React, { useEffect, useState } from "react";
import { Mail, MapPinned, UserPlus, QrCode, CheckCircle2, UserCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useStats } from "@/context/StatsContext";
import { Panel, Empty, ResBadge, DepositBadge, CompletionBadge, ExpiredLinkFlag, RowSearch, useQueryFilter, matchesQuery } from "@/pages/shared";
import RowActions from "@/components/RowActions";
import EditReservationDialog from "@/components/EditReservationDialog";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// Aankomsten = alle verwachte aankomsten (met én zonder plaats). Te laat bovenaan, rood.
// Zoekers (zonder plaats) tonen status 'Zoekend'. Enkel bewerken/wissen + zoeker-opties.
export default function Aankomsten() {
  const { t } = useLang();
  const { reload } = useStats();
  const [rows, setRows] = useState([]);
  const [guests, setGuests] = useState([]);
  const [zones, setZones] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [q, setQ] = useQueryFilter();
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [r, g, z, p] = await Promise.all([api.get("/reservations?filter=open"), api.get("/guests"), api.get("/zones"), api.get("/pitches")]);
      setRows(r.data); setGuests(g.data); setZones(z.data); setPitches(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete_reservation"))) return;
    try { await api.delete(`/reservations/${id}`); await Promise.all([load(), reload()]); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resendDeposit = async (res) => {
    try { const { data } = await api.post(`/reservations/${res.id}/resend-payment-request`); toast.success(`${t("deposit_resend_sent")} ${data.to}`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const printRegistration = (r) => window.open(`/print?res=${r.id}&lang=${r.language || "nl"}&map=1`, "_blank", "noopener");

  const [walkin, setWalkin] = useState(null);
  const [walkinBusy, setWalkinBusy] = useState(false);
  const [walkinResult, setWalkinResult] = useState(null);
  const [langs, setLangs] = useState([]);
  const openWalkin = () => {
    setWalkinResult(null);
    setWalkin({ first_name: "", last_name: "", email: "", language: "nl", hours: 48 });
    if (!langs.length) api.get("/public/guest-languages").then((r) => setLangs(r.data || [])).catch(() => {});
  };
  const createWalkin = async () => {
    if (!walkin.first_name || !walkin.email) { toast.error(t("walkin_need_fields")); return; }
    setWalkinBusy(true);
    try { const { data } = await api.post("/reservations/walkin", walkin); setWalkinResult(data); await Promise.all([load(), reload()]); toast.success(t("walkin_created")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setWalkinBusy(false); }
  };

  const visible = rows.filter((r) => matchesQuery(r, q));

  return (
    <Panel
      title={`${t("nav_aankomend")} (${visible.length}${q ? `/${rows.length}` : ""})`}
      testid="aankomsten-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("search_placeholder")} testid="aankomsten-search" />
          <Button variant="secondary" onClick={openWalkin} data-testid="walkin-button"><UserPlus className="h-4 w-4 mr-1" />{t("walkin")}</Button>
        </div>
      }
    >
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("arrival")}</TableHead>
              <TableHead>{t("departure")}</TableHead>
              <TableHead>{t("persons")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("deposit")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => {
              const late = r.arrival < today();
              const zoekend = !r.pitch_label && r.status === "reserved";
              return (
                <TableRow key={r.id} data-testid={`aankomst-row-${r.id}`}>
                  <TableCell className="font-medium">{r.guest_name}</TableCell>
                  <TableCell>{r.pitch_label ? <span className="font-semibold text-primary">{r.pitch_label}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className={late ? "font-medium text-rose-600 dark:text-rose-400" : "text-muted-foreground"}>
                    <div className="flex items-center gap-2">
                      {fmtDate(r.arrival)}
                      {late && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" data-testid={`late-badge-${r.id}`}>{t("overdue_label")}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(r.departure)}</TableCell>
                  <TableCell><div className="flex items-center gap-2">{r.persons}<CompletionBadge definitive={r.definitive} testid={`completion-${r.id}`} /><ExpiredLinkFlag reservation={r} compact testid={`link-expired-${r.id}`} /></div></TableCell>
                  <TableCell><ResBadge status={zoekend ? "zoekend" : r.status} /></TableCell>
                  <TableCell><DepositBadge reservation={r} testid={`deposit-badge-${r.id}`} onResend={resendDeposit} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {r.definitive === false && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" title={t("send_complete_link")} data-testid={`send-complete-link-${r.id}`}
                          onClick={() => api.post(`/reservations/${r.id}/send-complete-link`).then(({ data }) => toast.success(`${t("complete_link_sent")} ${data.to}`)).catch((e) => toast.error(apiError(e.response?.data?.detail)))}>
                          <Mail className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="outline" size="icon" className="h-8 w-8" title={t("print_registration")} data-testid={`print-reg-${r.id}`} onClick={() => printRegistration(r)}>
                        <UserCheck className="h-4 w-4" />
                      </Button>
                      <RowActions r={r} noPrint onEdit={setEditing} onDelete={(x) => remove(x.id)} />
                      {!r.pitch_label && (
                        <Button size="sm" onClick={() => setEditing(r)} data-testid={`assign-pitch-${r.id}`}>
                          <MapPinned className="h-4 w-4 mr-1" />{t("assign_pitch")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <EditReservationDialog reservation={editing} onClose={() => setEditing(null)} onSaved={async () => { await Promise.all([load(), reload()]); }} guests={guests} zones={zones} pitches={pitches} withDocuments arrivalToday />

      <Dialog open={!!walkin} onOpenChange={(o) => !o && setWalkin(null)}>
        <DialogContent className="max-w-md" data-testid="walkin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-4 w-4" />{t("walkin_title")}</DialogTitle>
          </DialogHeader>
          {!walkinResult ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{t("walkin_hint")}</p>
              <div className="grid grid-cols-2 gap-2">
                <input className="rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder={t("bf_first_name")} value={walkin?.first_name || ""} onChange={(e) => setWalkin({ ...walkin, first_name: e.target.value })} data-testid="walkin-first-name" />
                <input className="rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder={t("bf_last_name")} value={walkin?.last_name || ""} onChange={(e) => setWalkin({ ...walkin, last_name: e.target.value })} data-testid="walkin-last-name" />
              </div>
              <input className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" type="email" placeholder={t("bf_email")} value={walkin?.email || ""} onChange={(e) => setWalkin({ ...walkin, email: e.target.value })} data-testid="walkin-email" />
              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1 text-xs"><span className="text-muted-foreground">{t("language")}</span>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" value={walkin?.language || "nl"} onChange={(e) => setWalkin({ ...walkin, language: e.target.value })} data-testid="walkin-language">
                    {(langs.length ? langs : [{ code: "nl", label: "Nederlands" }]).map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </label>
                <label className="space-y-1 text-xs"><span className="text-muted-foreground">{t("walkin_hours")}</span>
                  <input className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" type="number" min="1" max="48" value={walkin?.hours ?? 48} onChange={(e) => setWalkin({ ...walkin, hours: Number(e.target.value) })} data-testid="walkin-hours" />
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-center" data-testid="walkin-result">
              <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" />{t("walkin_mail_sent")}</p>
              <div className="flex justify-center rounded-lg border border-border bg-white p-3"><QRCodeSVG value={walkinResult.url} size={160} level="M" /></div>
              <p className="text-xs text-muted-foreground">{t("walkin_qr_hint")}</p>
              <a href={walkinResult.url} target="_blank" rel="noopener noreferrer" className="block break-all text-[11px] text-primary underline" data-testid="walkin-link">{walkinResult.url}</a>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalkin(null)}>{walkinResult ? t("close") : t("cancel")}</Button>
            {!walkinResult && <Button onClick={createWalkin} disabled={walkinBusy} data-testid="walkin-create"><QrCode className="h-4 w-4 mr-1" />{t("walkin_create")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
